import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import { tableCustomStyles } from "../style";
import { IconDownload, IconMoodSad } from "@tabler/icons-react";
import { format } from "date-fns";

const FilterComponent = ({ filterText, onFilter, onClear }) => (
  <div className="ms-auto">
    Search:
    <div className="ms-2 d-inline-block">
      <div className="input-group input-group-sm">
        <input
          type="text"
          className="form-control form-control-sm"
          placeholder="Search"
          value={filterText}
          onChange={onFilter}
        />
        <button className="input-group-text btn" onClick={onClear}>
          x
        </button>
      </div>
    </div>
  </div>
);

const LoadingComponent = () => (
  <div className="text-center py-4">
    <div className="spinner-border text-primary" role="status" />
    <p className="mt-2 text-muted">Loading records...</p>
  </div>
);

const NoDataComponent = () => (
  <div className="empty">
    <div className="empty-icon">
      <IconMoodSad />
    </div>
    <p className="empty-title text-muted">No records found</p>
    <p className="empty-subtitle text-muted">
      Try adjusting your date range or search filter.
    </p>
  </div>
);

function convertArrayOfObjectsToCSV(array) {
  const columnDelimiter = ",";
  const lineDelimiter = "\n";
  const keys = Object.keys(array[0]);

  let result = keys.join(columnDelimiter) + lineDelimiter;
  array.forEach((item) => {
    let ctr = 0;
    keys.forEach((key) => {
      if (ctr > 0) result += columnDelimiter;
      const value = item[key] ?? "";
      result += `"${String(value).replace(/"/g, '""')}"`;
      ctr++;
    });
    result += lineDelimiter;
  });
  return result;
}

function downloadCSV(array) {
  if (!array || array.length === 0) return;

  const exportData = array.map(
    ({
      employeeId: EmployeeID,
      fullName: Name,
      doorLog: DoorLog,
      status: Status,
      manager: Manager,
      managerEmployeeId: ManagerEmployeeID,
      function: Function,
      organization: Organization,
      jobFamily: JobFamily,
    }) => ({ EmployeeID, Name, DoorLog, Status, Manager, ManagerEmployeeID, Function, Organization, JobFamily })
  );

  let csv = convertArrayOfObjectsToCSV(exportData);
  if (!csv.match(/^data:text\/csv/i)) csv = `data:text/csv;charset=utf-8,${csv}`;

  const link = document.createElement("a");
  link.setAttribute("href", encodeURI(csv));
  link.setAttribute("download", "r2w-onsite-report.csv");
  link.click();
}

const columns = [
  {
    name: "Employee ID",
    selector: (row) => row.employeeId,
    sortField: "EmployeeId",
    sortable: true,
    width: "120px",
  },
  {
    name: "Name",
    selector: (row) => row.fullName,
    sortField: "FullName",
    sortable: true,
    grow: 2,
  },
  {
    name: "Door Log",
    selector: (row) => row.doorLog,
    sortField: "DoorLog",
    sortable: true,
    grow: 1.5,
    cell: (row) =>
      row.doorLog ? format(new Date(row.doorLog), "MMM dd, yyyy HH:mm") : "--",
  },
  {
    name: "Status",
    selector: (row) => row.status,
    sortField: "Status",
    sortable: true,
  },
  {
    name: "Manager",
    selector: (row) => row.manager,
    sortField: "Manager",
    sortable: true,
    grow: 2,
  },
  {
    name: "Function",
    selector: (row) => row.function,
    sortField: "Function",
    sortable: true,
    grow: 1.5,
  },
  {
    name: "Organization",
    selector: (row) => row.organization,
    sortField: "Organization",
    sortable: true,
    grow: 1.5,
  },
  {
    name: "Job Family",
    selector: (row) => row.jobFamily,
    sortField: "JobFamily",
    sortable: true,
    grow: 1.5,
  },
];

export const TimelogTable = ({
  data = [],
  totalCount = 0,
  isLoading,
  onPageChange,
  onPerRowsChange,
  onSort,
  onSearchChange,
  resetPagination,
}) => {
  const [filterText, setFilterText] = useState("");

  const handleFilter = (e) => {
    setFilterText(e.target.value);
    onSearchChange(e.target.value);
  };

  const handleClear = () => {
    setFilterText("");
    onSearchChange("");
  };

  // Reset search input when parent signals a pagination reset
  useEffect(() => {
    setFilterText("");
  }, [resetPagination]);

  const subHeaderComponentMemo = React.useMemo(
    () => (
      <>
        <div className="col-md-6 text-start">
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => downloadCSV(data)}
            disabled={data.length === 0}
          >
            <IconDownload className="icon" />
            Export Page
          </button>
        </div>
        <div className="col-md-6 text-end">
          <FilterComponent
            onFilter={handleFilter}
            onClear={handleClear}
            filterText={filterText}
          />
        </div>
      </>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterText, data]
  );

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">Door Logs</h3>
        <div className="card-options">
          <span className="text-muted">{totalCount.toLocaleString()} records</span>
        </div>
      </div>
      <div className="table-responsive">
        <DataTable
          columns={columns}
          data={data}
          progressPending={isLoading}
          progressComponent={<LoadingComponent />}
          pagination
          paginationServer
          paginationTotalRows={totalCount}
          paginationResetDefaultPage={resetPagination}
          paginationRowsPerPageOptions={[10, 100, 1000]}
          paginationComponentOptions={{ selectAllRowsItem: true, selectAllRowsItemText: "All" }}
          onChangePage={onPageChange}
          onChangeRowsPerPage={onPerRowsChange}
          sortServer
          onSort={onSort}
          customStyles={tableCustomStyles}
          subHeader
          subHeaderComponent={subHeaderComponentMemo}
          noDataComponent={<NoDataComponent />}
        />
      </div>
    </div>
  );
};
